/* eslint-disable sonarjs/cognitive-complexity */
import React, {
  ChangeEvent,
  useRef,
  useState,
  useEffect,
  RefObject,
} from "react";
import InputFileButton from "../../FormUI/InputFileButton";

import {
  Alert,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid2,
} from "@mui/material";
import { WrapperStyled } from "../Extract/styles";
import {
  FormControlStyled,
  GridStyled,
  TextStyled,
  TextWrapperStyled,
} from "./styles";
import { fileAsBase64 } from "@/libs/utils";
import { LoadingButton } from "@mui/lab";
import { useDataFormContext } from "@/context/FormDataContext";
import { register } from "@/services/lead";
import { IRegisterResponse } from "@/services/index";
import { uploadImage } from "@/services/images";

const ATTACHMENT_MAX_SIZE = 6 * 1024 ** 2; // 6MBa

const MAX_ATTACHMENTS = 5;
/**
 * @returns Extract component
 */
export default function Extract(): React.JSX.Element {
  const [termAuthorizationData, setTermAuthorizationData] = useState(false);
  const [files, setFiles] = useState([] as Array<File>);
  const [filesAsBase64, setFilesAsBase64] = useState([] as Array<string>);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const inputFile = useRef<HTMLInputElement | null>(null);

  const {
    dataForm,
    metaDataTimes,
    setDataForm,
    setCurrentFormStep,
    setAlreadyShowed,
  } = useDataFormContext();

  /**
   * onFilePicked
   * @param {ChangeEvent<HTMLInputElement>} e
   * @return {void}
   */
  async function onFilePicked(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const target = e.target as HTMLInputElement;
    const newFiles = Array.from(target.files || []);

    if (
      newFiles.length > MAX_ATTACHMENTS ||
      newFiles.length + files.length > MAX_ATTACHMENTS
    ) {
      setError("Você pode enviar até 05 arquivos");
      return;
    }

    for (const file of newFiles) {
      const isFileInArray = files.some(
        (pickedFile) => pickedFile.name === file.name
      );

      if (file.size > ATTACHMENT_MAX_SIZE) {
        setError(
          `Por favor insira um arquivo com tamanho máximo de ${Number(ATTACHMENT_MAX_SIZE / 1024 ** 2).toFixed(0)} MB`
        );
        return;
      }

      if (!isFileInArray && files.length < 5) {
        const fileData = await fileAsBase64(file);
        setFiles((elem) => [...elem, file]);
        setFilesAsBase64((elem) => [...elem, fileData]);
        setError("");
      }
    }
    if (inputFile.current) {
      inputFile.current.value = "";
    }
  }

  const handleRemoveItem = (numberIndex: number) => {
    if (numberIndex > -1) {
      setFiles((prevState) =>
        prevState.filter((_, itemIndex) => itemIndex != numberIndex)
      );
      setFilesAsBase64((prevState) =>
        prevState.filter((_, itemIndex) => itemIndex != numberIndex)
      );
    }

    if (inputFile.current) {
      inputFile.current.value = "";
    }
  };

  const isValid = () => {
    if (files.length === 0) {
      if (!termAuthorizationData) {
        setIsLoading(false);
        setError(
          "Adicione uma imagem do extrato, ou selecione abaixo caso não tenho o extrato no momento"
        );
        return false;
      }
      if (termAuthorizationData) {
        return true;
      }
    }
    return true;
  };

  /**
   * getFileNames
   * @return {string}
   */
  function getFileNames(): string {
    const names = files.map((file) => {
      return file.name;
    });

    return names.join(" ");
  }

  /**
   * callRegister
   * @param {string} name
   * @param {string} path
   * @return {void}
   */
  async function callRegister(name = "", path = ""): Promise<void> {
    const formPayload = {
      ...dataForm,
      ...{
        formStep: 3,
        attachmentName: name,
        attachmentPath: path,
        metadata: {
          ...dataForm.metadata,
          formData: {
            ...metaDataTimes,
            finishedForm: String(new Date().valueOf()),
          },
        },
      },
    };
    setDataForm(formPayload);

    try {
      const registerResponse = await register(formPayload);
      const registerResponseData =
        registerResponse.data as unknown as IRegisterResponse;

      setIsLoading(false);

      if (registerResponseData.id) {
        setDataForm((current) => ({
          ...current,
          traceId: registerResponseData.traceId ?? current.traceId,
        }));
        setCurrentFormStep(3);
      } else {
        throw new Error("erro ao enviar dados");
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * handleSubmit
   * @param e
   */
  async function handleSubmit(e: any) {
    e.preventDefault();
    setIsLoading(true);

    if (isValid() && termAuthorizationData) {
      callRegister();
    } else if (isValid() && !termAuthorizationData) {
      setError("");

      let uploadedFiles = [] as Array<Promise<string>>;
      try {
        uploadedFiles = filesAsBase64.map(async (file) => {
          const result = await uploadImage(file);
          const fileURL = result.data.fileUrl as unknown as string;

          if (fileURL) {
            return fileURL;
          } else {
            throw new Error("erro ao enviar a imagem");
          }
        });
      } catch (error) {
        console.error(error);
      }

      const uploadedFilesResponse = await Promise.all(uploadedFiles);
      const fileNames = getFileNames();

      callRegister(fileNames, uploadedFilesResponse.join(" "));
    }
  }

  const inputRef = useRef(null) as any;
  useEffect(() => {
    if (inputRef.current) {
      const height = inputRef?.current?.offsetHeight;
      window.parent.postMessage(
        ["setHeight", `${height + (height * 55) / 100}`],
        "*"
      );
    }
  }, []);

  return (
    <form ref={inputRef} id="formExtract" onSubmit={(e) => handleSubmit(e)}>
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12 }}>
          <TextWrapperStyled>
            <TextStyled>
              Pra gente te mandar uma proposta certeira, anexe o extrato mais
              recente da sua cota.
            </TextStyled>
            <InputFileButton
              id="attachment"
              name="attachment"
              accept="image/*,application/pdf"
              inputRef={inputFile as unknown as RefObject<HTMLInputElement>}
              onChange={(e) => {
                onFilePicked(e);
              }}
              buttonId="attachmentButton"
              buttonLabel="enviar extrato"
              disabled={files.length === 5}
            />
            <p>Você pode enviar até 05 arquivos</p>

            {error && (
              <Alert severity="warning" sx={{ marginBottom: "10px" }}>
                {error}
              </Alert>
            )}
            {files.length > 0 && (
              <GridStyled container spacing={1}>
                {files.map((item, index) => {
                  return (
                    <Grid2 key={index} size={{ xs: 6, sm: 2 }}>
                      <Chip
                        label={item.name}
                        onDelete={() => handleRemoveItem(index)}
                      />
                    </Grid2>
                  );
                })}
              </GridStyled>
            )}
          </TextWrapperStyled>
        </Grid2>

        <Grid2 size={{ xs: 12 }}>
          <FormControlStyled>
            <FormControlLabel
              control={
                <Checkbox
                  name="termAuthorizationData"
                  id="termAuthorizationData"
                  onChange={() => {
                    setTermAuthorizationData(!termAuthorizationData);
                  }}
                />
              }
              label={"Não estou com o extrato no momento"}
            />
          </FormControlStyled>
        </Grid2>
        <Grid2 size={{ xs: 12 }}>
          <WrapperStyled>
            <Button
              variant="goBack"
              onClick={() => {
                setAlreadyShowed(true);
                setCurrentFormStep(1);
              }}
            >
              Voltar
            </Button>
            <LoadingButton loading={isLoading} variant="goFoward" type="submit">
              Continuar
            </LoadingButton>
          </WrapperStyled>
        </Grid2>
      </Grid2>
    </form>
  );
}
